/**
 * Quick Bytes filter panel.
 *
 * Same primitives as the Knowledge Archive panel - search lives in the
 * shared filter sections (`SourceLessonFilter`, `DomainTagFilter`,
 * `UserTagFilter`, `TimeWindowFilter`). "Due today" lives inside the
 * TimeWindowFilter so it stays grouped with the other temporal filters
 * and is reusable on Knowledge Archive.
 */

import { useMemo } from 'react';
import { X } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import {
  useDomainTagOptions,
  useUserTagOptions,
} from '@/hooks/mindspace/useMindspaceOptions';
import { useTagFilters } from '@/hooks/mindspace/useTagFilters';
import type { KnowledgeGraphNode } from '@/lib/api-client';
import { DomainTagFilter } from '@/components/features/mindspace/filters/DomainTagFilter';
import { SourceLessonFilter } from '@/components/features/mindspace/filters/SourceLessonFilter';
import { TimeWindowFilter } from '@/components/features/mindspace/filters/TimeWindowFilter';
import { UserTagFilter } from '@/components/features/mindspace/filters/UserTagFilter';
import { ActiveFilterChips } from '@/components/features/mindspace/filters/ActiveFilterChips';
import { HighlightsViewTabs } from '@/components/features/mindspace/quick-bytes/HighlightsViewTabs';
import {
  FilterLayoutContext,
  type FilterLayout,
} from '@/components/features/mindspace/filters/filterLayout';

export function QuickBytesFilterPanel({
  showViewTabs = true,
  layout = 'panel',
}: {
  /** False on a phone, where the view switch is on the page itself. */
  showViewTabs?: boolean;
  /** `'sheet'` - the phone's: every section open, quickest first, no chip
   *  strip and no Clear footer (the sheet's footer has it). */
  layout?: FilterLayout;
} = {}) {
  const filterState = useTagFilters();
  const { setFilters, hasAnyFilter, bookmarked } = filterState;

  // The shared `DomainTagFilter` / `UserTagFilter` components have a
  // "dim-when-zero" hint that fades any tag NOT present in the passed
  // `nodes` payload. On the Knowledge Archive that hint pruned tags
  // unhelpful under current filters. Quick Bytes doesn't expose a
  // node payload (highlights are flat carousels, not graph nodes), so
  // passing `nodes={undefined}` made every tag dim by default.
  //
  // Workaround: hand the filters a single fake node carrying every
  // domain + user tag known to the user, so `availableInView` is fully
  // populated and the dim hint never fires. `useDomainTagOptions` /
  // `useUserTagOptions` are the same queries the filters themselves
  // use internally - React Query dedupes the fetch.
  const { data: domainOptions = [] } = useDomainTagOptions();
  const { data: userOptions = [] } = useUserTagOptions();
  const allTagsNode = useMemo<KnowledgeGraphNode>(
    () => ({
      id: '__quick-bytes-all-tags__',
      name: '',
      val: 0,
      category: null,
      importance: null,
      source_lessons: null,
      description: null,
      domain_tags: domainOptions.map(o => o.tag),
      user_tags: userOptions.map(o => o.tag),
      level: null,
      stability: null,
      last_reviewed_at: null,
      created_at: null,
      review_count: null,
      source_document_ids: null,
      is_orphaned: null,
    }),
    [domainOptions, userOptions]
  );
  const stubNodes = useMemo(() => [allTagsNode], [allTagsNode]);

  const clearAll = () =>
    setFilters({
      domain: [],
      user: [],
      match: 'any',
      q: '',
      lessons: [],
      documents: [],
      timeAxis: 'last_reviewed_at',
      window: 'all',
      dueToday: false,
      bookmarked: false,
    });

  const activeCount =
    (filterState.q ? 1 : 0) +
    filterState.selectedLessons.length +
    filterState.selectedDomain.length +
    filterState.selectedUser.length +
    (filterState.window !== 'all' ? 1 : 0) +
    (filterState.dueToday ? 1 : 0) +
    (filterState.bookmarked ? 1 : 0);

  if (layout === 'sheet') {
    return (
      <FilterLayoutContext.Provider value='sheet'>
        <TimeWindowFilter />
        <DomainTagFilter nodes={stubNodes} />
        <UserTagFilter nodes={stubNodes} />
        <SourceLessonFilter />
      </FilterLayoutContext.Provider>
    );
  }

  return (
    <div className='flex h-full flex-col'>
      {/* View switcher sits at the top - bookmarks are a personal
          collection, framed as a "which view am I in?" choice rather
          than a filter to flip on. Visual style mirrors the
          `TabbedInterface` (variant='default') used by SourcesPanel:
          padded rounded-lg container, equal-width tabs with rounded-md
          active pill, gray active fill instead of pure white. Sized
          slightly more compact (text-sm/py-2) to fit the filter panel
          chrome. */}
      {showViewTabs && (
        <HighlightsViewTabs
          bookmarked={bookmarked}
          onChange={next => setFilters({ bookmarked: next })}
        />
      )}

      <ActiveFilterChips />

      <div className='min-h-0 flex-1 overflow-y-auto py-1'>
        <SourceLessonFilter />
        {/* `stubNodes` carries every known domain/user tag in a single
            fake node so `availableInView` covers them all and no tag
            renders dimmed. See the comment above the `allTagsNode`
            memo for why this workaround exists. */}
        <DomainTagFilter nodes={stubNodes} />
        <UserTagFilter nodes={stubNodes} />
        <TimeWindowFilter />
      </div>

      {hasAnyFilter && (
        <div className='flex-shrink-0 p-3'>
          <Button
            variant='outline'
            size='sm'
            onClick={clearAll}
            className='w-full gap-1.5'
          >
            <X className='h-3 w-3' />
            Clear {activeCount} filter{activeCount === 1 ? '' : 's'}
          </Button>
        </div>
      )}
    </div>
  );
}
